import './tools/tools.css';
import { fromStored, type ImageContext, type StoredAnnotation, toStored } from './annotation-format';
import type { InstanceRef, SeriesRef } from './dicomweb';
import type { ViewerShell } from './shell';
import { isImageSeries } from './study';
import { currentLeftTool, setAnnotationsReadOnly, setLeftTool, toolMode } from './tools';
import { csCore, csTools, type MeasurementData, type Readout } from './tools/cstypes';
import { setDialogHost } from './tools/dialogs';
import type { Viewport } from './viewport';
import { isCrossProxy } from './tools/crossview';

/**
 * Measurements and the "Anotaciones" workspace (right drawer, spec section 1.3): one card per
 * measurement of the active viewport's series, with its value, slice and a visibility toggle.
 * The server part (Guardar / Versión / Ver versión against ris) attaches through `attachWorkspaceServer`
 * (src/ris/annotations-sync.ts); without ris "Guardar" is shown disabled.
 */

/** cornerstone-tools tool name → IMEXHS card type (spec section 1.3, spelling kept). */
const TYPE_BY_TOOL: Record<string, string> = {
  Probe: 'DENSIDAD',
  Angle: 'ANGULO',
  Length: 'LINEA',
  EllipticalRoi: 'ELIPSE',
  RectangleRoi: 'RECTÁNGULO',
  FreehandMouse: 'POLÍGONO',
  Coxometry: 'COXOMETRÍA',
  CardiothoracicIndex: 'ÍNDICE-CARDIOTORÁCICO',
  Bidirectional: 'BIDIRECCIONAL',
  ParallelDistance: 'DISTANCIA-PARALELA',
  InsallSalvati: 'INSALO-SALVATI',
  FemoralAnteversion: 'ANTEVERSIÓN-FEMORAL',
  CobbAngle: 'ÁNGULO-COBB',
  ArrowAnnotate: 'FLECHA',
  TextMarker: 'ETIQUETA DE COLUMA',
  TextAnnotation: 'TEXTO',
};

const MEASUREMENT_TOOL_NAMES = Object.keys(TYPE_BY_TOOL);
const SAVE_UNAVAILABLE = 'Disponible cuando el visor se conecte a ris-api';
const NO_DATA = 'No data';
const NOT_SAVED = 'No se guardará';
const TOGGLE_TITLE = { open: 'Ocultar anotaciones', closed: 'Mostrar anotaciones' } as const;

interface Card {
  toolName: string;
  data: MeasurementData;
  imageIndex: number;
  /** False when "Guardar" would leave it out (still being drawn, zero area, empty text). */
  savable: boolean;
}

interface Workspace {
  heading: HTMLElement;
  save: HTMLButtonElement;
  banner: HTMLElement;
  list: HTMLElement;
}

/** What the ris sync offers the panel: "Guardar", "Versión" and "Ver versión". */
export interface WorkspaceServer {
  /** The session may save (viewer:annotate); otherwise "Guardar" stays disabled. */
  canSave: boolean;
  save(): void;
  showVersions(): void;
  viewVersion(): void;
}

const SAVE_TITLE = 'Guardar las anotaciones en el servidor';
const SAVE_DENIED = 'No dispone de los permisos necesarios para guardar anotaciones';
const VERSIONS_TITLE = 'Ver las versiones guardadas';
const VIEW_VERSION_TITLE = 'Ver una versión guardada (solo lectura)';

let shellRef: ViewerShell | null = null;
let serverCanSave = false;
let workspace: Workspace | null = null;
let refreshQueued = false;
let dirty = true;
/** What the list currently shows, so renders that change nothing do not rebuild it. */
let shownSignature = '';

/** One saved measurement with its image address (ris viewer-api-contract §4 item). */
export interface StoredItem extends StoredAnnotation {
  study_uid: string;
  series_uid: string;
  sop_uid: string;
  /** 1-based frame within the instance (1 for single-frame instances). */
  frame: number;
  /** 1-based position in the series stack, and the stack size (IMEXHS "slice/slices_number"). */
  slice: number;
  slices_number: number;
  modality: string;
  title: string;
}

export interface LoadOptions {
  /** Draw the set without letting anyone edit it (a saved version being viewed). */
  readOnly: boolean;
}

export interface LoadResult {
  loaded: number;
  /** Items whose series/image is not in any study of the browser, or whose type/points are invalid. */
  skipped: StoredItem[];
}

declare global {
  interface Window {
    /** Test hook: drive tools from Playwright without the toolbar. */
    __viewerTools?: {
      setLeftTool: typeof setLeftTool;
      currentLeftTool: typeof currentLeftTool;
      toolMode: typeof toolMode;
      clearAllMeasurements: typeof clearAllMeasurements;
      clearCurrentSlice: typeof clearCurrentSlice;
      collectStoredAnnotations: typeof collectStoredAnnotations;
      loadStoredAnnotations: typeof loadStoredAnnotations;
      toStored: typeof toStored;
      fromStored: typeof fromStored;
    };
  }
}

export function install(shell: ViewerShell): void {
  shellRef = shell;
  setDialogHost(shell.regions.overlayLayer);
  workspace = buildDrawer(shell.regions.rightDrawer);
  addToggleButton(shell);
  shell.on('layout-changed', ({ viewports }) => viewports.forEach(watchViewport));
  shell.on('active-changed', scheduleRefresh);
  shell.on('series-shown', scheduleRefresh);
  // Values, text and completed shapes land on the render after the tool event (or with no event at all).
  shell.on('image-rendered', ({ viewport }) => {
    if (viewport === shell.activeViewport()) scheduleRefresh();
  });
  window.__viewerTools = {
    setLeftTool,
    currentLeftTool,
    toolMode,
    clearAllMeasurements,
    clearCurrentSlice,
    collectStoredAnnotations,
    loadStoredAnnotations,
    toStored,
    fromStored,
  };
}

/** " Limpiar ": remove every measurement in every viewport. */
export function clearAllMeasurements(): void {
  const state = csTools().globalImageIdSpecificToolStateManager.saveToolState();
  for (const imageState of Object.values(state)) {
    if (imageState) for (const name of MEASUREMENT_TOOL_NAMES) delete imageState[name];
  }
  redrawAll();
}

/** Q: remove the measurements drawn on the slice shown in `viewport`. */
export function clearCurrentSlice(viewport: Viewport): void {
  const imageId = viewport.currentImage()?.imageId;
  if (!imageId) return;
  const imageState = csTools().globalImageIdSpecificToolStateManager.saveToolState()[imageId];
  if (imageState) for (const name of MEASUREMENT_TOOL_NAMES) delete imageState[name];
  redrawAll();
}

/** Where an imageId sits: its series, instance, frame and stack position. */
interface ImageAddress {
  studyUID: string;
  series: SeriesRef;
  instance: InstanceRef;
  frame: number;
  index: number;
  count: number;
}

/** Every image of every image series in the browser, by imageId. */
function imageAddresses(shell: ViewerShell): Map<string, ImageAddress> {
  const addresses = new Map<string, ImageAddress>();
  for (const study of shell.studies()) {
    for (const series of study.series) {
      if (!isImageSeries(series)) continue;
      const imageIds = shell.imageIdsFor(series);
      let index = 0;
      for (const instance of series.instances) {
        for (let frame = 1; frame <= instance.numberOfFrames; frame++, index++) {
          const imageId = imageIds[index];
          if (imageId && !addresses.has(imageId)) {
            addresses.set(imageId, { studyUID: study.studyInstanceUID, series, instance, frame, index, count: imageIds.length });
          }
        }
      }
    }
  }
  return addresses;
}

interface CachedImages {
  imageCache?: Record<string, { image?: { rowPixelSpacing?: number; columnPixelSpacing?: number } } | undefined>;
}

/** Spacing the tools measured with: image plane metadata first, else the decoded image. */
function imageContextOf(imageId: string, modality: string): ImageContext {
  const plane = csCore().metaData.get('imagePlaneModule', imageId) as { rowPixelSpacing?: number; columnPixelSpacing?: number } | undefined;
  const image = (csCore().imageCache as unknown as CachedImages).imageCache?.[imageId]?.image;
  return {
    rowPixelSpacing: plane?.rowPixelSpacing || image?.rowPixelSpacing,
    columnPixelSpacing: plane?.columnPixelSpacing || image?.columnPixelSpacing,
    modality,
  };
}

/**
 * Every measurement on every image of the studies in the browser, in the wire format, with its image
 * address. Measurements that cannot be saved (still being drawn, zero area, empty text) are left out,
 * as are marks on images outside the browser's series (MPR / derived views).
 */
export function collectStoredAnnotations(): StoredItem[] {
  if (!shellRef) return [];
  const addresses = imageAddresses(shellRef);
  const state = csTools().globalImageIdSpecificToolStateManager.saveToolState();
  const items: StoredItem[] = [];
  for (const [imageId, imageState] of Object.entries(state)) {
    const address = addresses.get(imageId);
    if (!imageState || !address) continue;
    const context = imageContextOf(imageId, address.series.modality);
    for (const toolName of MEASUREMENT_TOOL_NAMES) {
      for (const data of imageState[toolName]?.data ?? []) {
        if (isCrossProxy(data)) continue; // display-only half of a cross-image measurement
        const stored = toStored(toolName, data, context);
        if (!stored) continue;
        items.push({
          ...stored,
          study_uid: address.studyUID,
          series_uid: address.series.seriesInstanceUID,
          sop_uid: address.instance.sopInstanceUID,
          frame: address.frame,
          slice: address.index + 1,
          slices_number: address.count,
          modality: address.series.modality,
          title: typeof data.title === 'string' ? data.title : '',
        });
      }
    }
  }
  return items.sort((a, b) => a.series_uid.localeCompare(b.series_uid) || a.slice - b.slice);
}

/** The imageId an item was saved on: by SOP instance + frame, else by its stack position. */
function imageIdFor(shell: ViewerShell, item: StoredItem): string | undefined {
  for (const study of shell.studies()) {
    if (item.study_uid && study.studyInstanceUID !== item.study_uid) continue;
    const series = study.series.find((s) => s.seriesInstanceUID === item.series_uid && isImageSeries(s));
    if (!series) continue;
    const imageIds = shell.imageIdsFor(series);
    let offset = 0;
    for (const instance of series.instances) {
      if (instance.sopInstanceUID === item.sop_uid) {
        const frame = Math.min(Math.max(1, item.frame || 1), instance.numberOfFrames);
        return imageIds[offset + frame - 1];
      }
      offset += instance.numberOfFrames;
    }
    return item.slice >= 1 ? imageIds[item.slice - 1] : undefined;
  }
  return undefined;
}

/**
 * Replaces every measurement with `items` (a saved version). With `readOnly` the set is drawn but
 * cannot be edited and no measurement tool can be selected; loading with readOnly false lifts that.
 * Values are recomputed from the geometry when the images render.
 */
export function loadStoredAnnotations(items: StoredItem[], options: LoadOptions): LoadResult {
  const skipped: StoredItem[] = [];
  const shell = shellRef;
  clearAllMeasurements();
  setAnnotationsReadOnly(options.readOnly);
  if (!shell) return { loaded: 0, skipped: [...items] };
  const state = csTools().globalImageIdSpecificToolStateManager.saveToolState();
  let loaded = 0;
  for (const item of items) {
    const imageId = imageIdFor(shell, item);
    const measurement = imageId ? fromStored(item, { modality: item.modality }) : null;
    if (!imageId || !measurement) {
      skipped.push(item);
      continue;
    }
    const { toolName, data } = measurement;
    data.readOnly = options.readOnly;
    if (item.title) data.title = item.title;
    const imageState = (state[imageId] ??= {});
    (imageState[toolName] ??= { data: [] }).data.push(data);
    loaded++;
  }
  redrawAll();
  return { loaded, skipped };
}

function redrawAll(): void {
  shellRef?.viewports().forEach((vp) => {
    if (vp.currentImage()) window.cornerstone.updateImage(vp.element);
  });
  scheduleRefresh();
}

function watchViewport(viewport: Viewport): void {
  const { EVENTS } = csTools();
  for (const type of [EVENTS.MEASUREMENT_ADDED, EVENTS.MEASUREMENT_MODIFIED, EVENTS.MEASUREMENT_REMOVED]) {
    viewport.element.addEventListener(type, scheduleRefresh);
  }
  scheduleRefresh();
}

/**
 * Values are computed while a measurement is drawn, so the list is rebuilt on the frame after the
 * render that follows a change (at most once per frame, however many events arrive).
 */
function scheduleRefresh(): void {
  dirty = true;
  if (refreshQueued || !shellRef?.isRightDrawerOpen()) return;
  refreshQueued = true;
  requestAnimationFrame(() =>
    requestAnimationFrame(() => {
      refreshQueued = false;
      refresh();
    }),
  );
}

function buildDrawer(drawer: HTMLElement): Workspace {
  drawer.replaceChildren();
  drawer.classList.add('annot-drawer');
  const title = document.createElement('h2');
  title.className = 'drawer-title';
  title.textContent = 'ANOTACIONES';
  const section = document.createElement('section');
  section.className = 'annot-workspace';
  const heading = document.createElement('div');
  heading.className = 'annot-workspace-head';
  const name = document.createElement('h3');
  name.textContent = 'Área de trabajo';
  const save = document.createElement('button');
  save.type = 'button';
  save.className = 'annot-save';
  save.textContent = 'Guardar';
  save.disabled = true;
  save.title = SAVE_UNAVAILABLE;
  heading.append(name, save);
  const banner = document.createElement('div');
  banner.className = 'annot-banner';
  banner.hidden = true;
  const list = document.createElement('div');
  list.className = 'annot-list';
  list.setAttribute('role', 'list');
  section.append(heading, banner, list);
  drawer.append(title, section);
  return { heading, save, banner, list };
}

function headingButton(label: string, className: string, title: string, onClick: () => void): HTMLButtonElement {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = className;
  button.textContent = label;
  button.title = title;
  button.addEventListener('click', onClick);
  return button;
}

/** Connects the panel to ris: enables "Guardar" (when allowed) and adds "Versión" / "Ver versión". */
export function attachWorkspaceServer(server: WorkspaceServer): void {
  if (!workspace) return;
  const { heading, save } = workspace;
  serverCanSave = server.canSave;
  save.disabled = !server.canSave;
  save.title = server.canSave ? SAVE_TITLE : SAVE_DENIED;
  save.addEventListener('click', () => server.save());
  heading.querySelectorAll('.annot-version, .annot-view-version').forEach((el) => el.remove());
  heading.append(
    headingButton('Versión', 'annot-version', VERSIONS_TITLE, () => server.showVersions()),
    headingButton('Ver versión', 'annot-view-version', VIEW_VERSION_TITLE, () => server.viewVersion()),
  );
}

/** "Guardar" off while a save runs or a saved version is shown read-only. */
export function setSaveEnabled(enabled: boolean): void {
  if (workspace) workspace.save.disabled = !(enabled && serverCanSave);
}

/** The read-only banner ("Viendo versión N (solo lectura)") with its way back, or null to remove it. */
export function setWorkspaceBanner(banner: { text: string; actionLabel: string; onAction: () => void } | null): void {
  if (!workspace) return;
  const host = workspace.banner;
  host.replaceChildren();
  host.hidden = banner === null;
  if (!banner) return;
  const text = document.createElement('span');
  text.className = 'annot-banner-text';
  text.textContent = banner.text;
  host.append(text, headingButton(banner.actionLabel, 'annot-banner-action', banner.actionLabel, banner.onAction));
}

function addToggleButton(shell: ViewerShell): void {
  const button = document.createElement('button');
  button.type = 'button';
  button.id = 'xv-annotations-toggle-btn';
  button.className = 'toolbar-button annot-toggle';
  button.textContent = 'Anotaciones';
  button.setAttribute('aria-label', 'Anotaciones'); // icon-only on the compact chrome
  const sync = () => {
    const open = shell.isRightDrawerOpen();
    button.classList.toggle('active', open);
    button.setAttribute('aria-pressed', String(open));
    button.title = open ? TOGGLE_TITLE.open : TOGGLE_TITLE.closed;
  };
  button.addEventListener('click', () => {
    shell.setRightDrawer(!shell.isRightDrawerOpen());
    sync();
    if (shell.isRightDrawerOpen() && dirty) scheduleRefresh();
  });
  shell.on('drawer-changed', sync); // the compact chrome closes the drawer on its own (scrim, mode change)
  sync();
  shell.regions.topNav.appendChild(button);
}

/** Measurements of every image of the active viewport's series, in slice order. */
function collectCards(viewport: Viewport): Card[] {
  const stack = viewport.currentStack;
  if (!stack) return [];
  const state = csTools().globalImageIdSpecificToolStateManager.saveToolState();
  const cards: Card[] = [];
  stack.imageIds.forEach((imageId, imageIndex) => {
    const imageState = state[imageId];
    if (!imageState) return;
    const context = imageContextOf(imageId, stack.series.modality);
    for (const toolName of MEASUREMENT_TOOL_NAMES) {
      for (const data of (imageState[toolName]?.data ?? []).filter((d) => !isCrossProxy(d))) cards.push({ toolName, data, imageIndex, savable: toStored(toolName, data, context) !== null });
    }
  });
  return cards;
}

function refresh(): void {
  const viewport = shellRef?.activeViewport();
  if (!workspace || !viewport) return;
  dirty = false;
  const cards = collectCards(viewport);
  const signature = signatureOf(viewport, cards);
  if (signature === shownSignature) return;
  shownSignature = signature;
  const { list } = workspace;
  list.replaceChildren();
  if (cards.length === 0) {
    const empty = document.createElement('p');
    empty.className = 'annot-empty';
    const series = viewport.currentStack?.series;
    const seriesName = series ? series.description || `Serie ${series.seriesNumber}` : 'La serie';
    empty.textContent = `${seriesName} no tiene anotaciones por el momento en el área de trabajo`;
    list.appendChild(empty);
    return;
  }
  cards.forEach((card) => list.appendChild(renderCard(card, viewport)));
}

function signatureOf(viewport: Viewport, cards: Card[]): string {
  const series = viewport.currentStack?.series.seriesInstanceUID ?? '';
  return JSON.stringify([series, cards.map((c) => [c.toolName, c.imageIndex, c.data.visible, c.savable, readoutOf(c.data)])]);
}

function readoutOf(data: MeasurementData): Readout[] {
  if (data.readout && data.readout.length > 0) return data.readout;
  if (typeof data.text === 'string' && data.text) return [{ label: 'Texto:', value: data.text }];
  return [];
}

function line(label: string, value: string): HTMLElement {
  const row = document.createElement('div');
  row.className = 'annot-value';
  const strong = document.createElement('span');
  strong.className = 'annot-label';
  strong.textContent = label;
  const text = document.createElement('span');
  text.textContent = value;
  row.append(strong, ' ', text);
  return row;
}

function renderCard(card: Card, viewport: Viewport): HTMLElement {
  const item = document.createElement('div');
  item.className = 'annot-card';
  item.setAttribute('role', 'listitem');
  item.dataset.tool = card.toolName;
  item.classList.toggle('hidden-annotation', card.data.visible === false);
  const head = document.createElement('div');
  head.className = 'annot-card-head';
  const type = document.createElement('span');
  type.className = 'annot-type';
  type.textContent = TYPE_BY_TOOL[card.toolName];
  head.append(type, visibilityToggle(card));
  const body = document.createElement('button');
  body.type = 'button';
  body.className = 'annot-card-body';
  body.title = 'Ir al corte';
  const values = readoutOf(card.data);
  if (values.length === 0) body.appendChild(line(NO_DATA, ''));
  values.forEach((v) => body.appendChild(line(v.label, v.value)));
  body.appendChild(line('Corte:', String(card.imageIndex + 1)));
  if (!card.savable) body.appendChild(line(NOT_SAVED, ''));
  body.addEventListener('click', () => goToSlice(viewport, card.imageIndex));
  item.append(head, body);
  return item;
}

function visibilityToggle(card: Card): HTMLButtonElement {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'annot-visibility';
  const visible = card.data.visible !== false;
  button.textContent = visible ? 'Ocultar' : 'Mostrar';
  button.setAttribute('aria-pressed', String(!visible));
  button.title = visible ? 'Ocultar la anotación en la imagen' : 'Mostrar la anotación en la imagen';
  button.addEventListener('click', () => {
    card.data.visible = card.data.visible === false;
    card.data.active = false;
    redrawAll();
  });
  return button;
}

function goToSlice(viewport: Viewport, index: number): void {
  if (viewport.currentIndex() === index) return;
  window.cornerstoneTools.scrollToIndex(viewport.element, index);
}
